import { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const detectType = (file) => {
  const ct = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp|tif|tiff|svg|heic)$/i.test(name)) return "image";
  if (ct.startsWith("video/") || /\.(mp4|mov|webm|avi|mkv|m4v|mpeg|mpg)$/i.test(name)) return "video";
  if (ct.startsWith("audio/") || /\.(mp3|wav|ogg|flac|m4a|aac|aiff)$/i.test(name)) return "audio";
  if (ct === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
  return "embed";
};

const formatBytes = (b) => {
  if (!b) return "—";
  const k = 1024, units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
};

export default function BulkIngest({ dossiers = [], onDone }) {
  const [dossier, setDossier] = useState("");
  const [status, setStatus] = useState("ARCHIVED");
  const [queue, setQueue] = useState([]); // [{file, type, state, ak_id?, title?, error?}]
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setQueue((q) => [
      ...q,
      ...arr.map((f) => ({
        id: (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`),
        file: f,
        type: detectType(f),
        state: "queued",
        size: f.size,
      })),
    ]);
  };

  const removeItem = (id) => setQueue((q) => q.filter((x) => x.id !== id));
  const clearAll = () => setQueue([]);

  const ingest = async () => {
    if (busy || !queue.length) return;
    const pending = queue.filter((q) => q.state === "queued" || q.state === "error");
    if (!pending.length) return;
    setBusy(true);
    const pendingIds = new Set(pending.map((p) => p.id));

    const fd = new FormData();
    pending.forEach((q) => fd.append("files", q.file));
    setQueue((q) => q.map((x) => (pendingIds.has(x.id) ? { ...x, state: "uploading" } : x)));

    try {
      const params = {};
      if (dossier) params.dossier_slug = dossier;
      if (status) params.status = status;
      const { data } = await api.post("/admin/ingest-bulk", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });

      const createdMap = new Map((data.created || []).map((c) => [c.original_filename, c]));
      const errorMap = new Map((data.errors || []).map((e) => [e.filename, e.error]));

      setQueue((q) =>
        q.map((x) => {
          if (!pendingIds.has(x.id)) return x;
          const created = createdMap.get(x.file.name);
          if (created) {
            return {
              ...x,
              state: "done",
              ak_id: created.ak_id,
              title: created.title,
              author: created.author,
              year: created.year,
              media_id: created.id,
            };
          }
          return { ...x, state: "error", error: errorMap.get(x.file.name) || "Failed" };
        })
      );

      toast.success(`Ingested ${data.created.length} item${data.created.length === 1 ? "" : "s"}`);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk ingest failed");
      setQueue((q) => q.map((x) => (pendingIds.has(x.id) ? { ...x, state: "error", error: "Upload failed" } : x)));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const doneCount = queue.filter((q) => q.state === "done").length;

  return (
    <div className="hairline p-6 space-y-5" data-testid="bulk-ingest-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="heavy text-4xl leading-none">Bulk ingest.</h2>
          <div className="mono text-[11px] text-ink-muted mt-2">
            Drop any media — images, videos, audio, PDFs. Only the file is required.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="bulk-dossier"
            value={dossier}
            onChange={(e) => setDossier(e.target.value)}
            className="input-mono w-auto"
            title="Optional dossier"
          >
            <option value="">Dossier · Unsorted</option>
            {dossiers.map((d) => (
              <option key={d.slug} value={d.slug}>{d.slug}</option>
            ))}
          </select>
          <select
            data-testid="bulk-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="input-mono w-auto"
            title="Default status"
          >
            <option value="ARCHIVED">Archived</option>
            <option value="DECLASSIFIED">Declassified</option>
            <option value="LIVE">Live</option>
          </select>
        </div>
      </div>

      {/* Drop zone */}
      <div
        data-testid="bulk-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`crosshair p-12 text-center transition-colors bg-white text-black ${
          dragOver ? "opacity-90" : "hover:opacity-95"
        }`}
        style={{ border: dragOver ? "3px dashed #000" : "3px dashed rgba(0,0,0,0.25)" }}
      >
        <input
          ref={inputRef}
          data-testid="bulk-file-input"
          type="file"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="heavy text-4xl mb-2">↘ Drop transmissions here</div>
        <div className="mono text-[11px] uppercase tracking-[0.14em] text-black/60">
          or click to browse · select multiple files
        </div>
        <div className="mono text-[10px] text-black/50 mt-3">
          PDFs auto-extract title · author · year &nbsp;//&nbsp; others use filename
        </div>
      </div>

      {/* Queue */}
      {queue.length > 0 && (
        <div className="space-y-2" data-testid="bulk-queue">
          <div className="flex items-center justify-between">
            <div className="label-overline">
              // Queue · {queue.length} file{queue.length === 1 ? "" : "s"} {doneCount > 0 && `· ${doneCount} ingested`}
            </div>
            <div className="flex items-center gap-2">
              {queue.some((q) => q.state === "done") && (
                <button
                  onClick={() => setQueue((q) => q.filter((x) => x.state !== "done"))}
                  className="pill pill-sm"
                  data-testid="bulk-clear-done"
                >
                  Clear done
                </button>
              )}
              <button
                onClick={clearAll}
                className="pill pill-sm"
                data-testid="bulk-clear-all"
              >
                Clear all
              </button>
            </div>
          </div>
          <div className="space-y-1 max-h-[360px] overflow-y-auto">
            {queue.map((q, i) => (
              <QueueRow key={q.id} idx={i} item={q} onRemove={() => removeItem(q.id)} />
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="mono text-[10px] text-ink-muted">
          {dossier ? <>→ Will ingest into <span className="text-white">/{dossier}</span></> : "→ Will ingest into /unsorted (auto-created)"}
        </div>
        <button
          onClick={ingest}
          disabled={busy || queue.filter((q) => q.state === "queued" || q.state === "error").length === 0}
          data-testid="bulk-ingest-submit"
          className="pill pill-active disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Ingesting…" : `Ingest ${queue.filter((q) => q.state === "queued" || q.state === "error").length} →`}
        </button>
      </div>
    </div>
  );
}

const QueueRow = ({ idx, item, onRemove }) => {
  const colors = {
    queued: "text-ink-muted",
    uploading: "text-white",
    done: "text-emerald-400",
    error: "text-red-400",
  };
  const labels = {
    queued: "Queued",
    uploading: "Ingesting…",
    done: "✓ Ingested",
    error: "Error",
  };
  return (
    <div
      data-testid={`bulk-queue-row-${idx}`}
      className="hairline px-3 py-2 flex items-center gap-3"
    >
      <span className="pill pill-sm !py-1 !px-3 !text-[9px]">{item.type.toUpperCase()}</span>
      <div className="flex-1 min-w-0">
        <div className="mono text-[12px] truncate" title={item.file.name}>{item.file.name}</div>
        <div className="mono text-[10px] text-ink-faint flex items-center gap-3 flex-wrap">
          <span>{formatBytes(item.size)}</span>
          {item.ak_id && <span className="text-white">{item.ak_id}</span>}
          {item.title && <span className="truncate">→ {item.title}</span>}
          {item.author && <span>· {item.author}</span>}
          {item.year && <span>· {item.year}</span>}
          {item.error && <span className="text-red-400">· {item.error}</span>}
        </div>
      </div>
      <span className={`mono text-[10px] uppercase tracking-[0.14em] ${colors[item.state]}`}>{labels[item.state]}</span>
      {item.state !== "uploading" && (
        <button
          onClick={onRemove}
          className="mono text-[14px] text-ink-faint hover:text-red-400 px-2"
          data-testid={`bulk-queue-remove-${idx}`}
          aria-label="Remove"
        >×</button>
      )}
    </div>
  );
};
